import {
  Badge,
  Cell,
  Column,
  RadioIndicator,
  Row,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
  cn,
  type Selection,
} from '@skupreme/shared/ui';
import { OwnCarrierAccountMark } from '../../legacy-bridge';
import { DateUtility } from '@utility/date';
import { rateText, type RateOption } from '../../lib/rates';

export type RateListProps = {
  options: RateOption[];
  /** Which row is picked; `any` means the warehouse chooses the service. */
  selectedKey: string;
  onSelect: (option: RateOption | null) => void;
  cheapestKey?: string;
  currency?: string;
  isLoading?: boolean;
  /** The workspace, whose icon marks rates quoted on its own account. */
  orgName?: string;
  orgLogo?: string;
  /** Relabels must name the carrier tuple; the warehouse may not choose later. */
  allowAutomatic?: boolean;
};

const NUMERIC = 'sk:text-right sk:whitespace-nowrap';

export function RateList({
  options,
  selectedKey,
  onSelect,
  cheapestKey,
  currency,
  isLoading,
  orgName,
  orgLogo,
  allowAutomatic = true,
}: RateListProps) {
  if (isLoading) {
    return (
      <div className="sk:flex sk:flex-col sk:gap-2">
        {[0, 1, 2].map(row => (
          <div
            key={row}
            className="sk:h-9 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary"
          />
        ))}
      </div>
    );
  }

  /* Single selection still toggles, so an emptied set means the same row again. */
  const selectionChanged = (keys: Selection) => {
    if (keys === 'all') {
      return;
    }

    const [key] = [...keys];

    if (!key) {
      return;
    }

    onSelect(options.find(option => option.key === key) || null);
  };

  return (
    <TableContainer className="sk:overflow-visible">
      <Table
        aria-label="Shipping method"
        selectionMode="single"
        disabledBehavior="selection"
        selectedKeys={[selectedKey]}
        disabledKeys={options
          .filter(option => option.isDisabled)
          .map(option => option.key)}
        onSelectionChange={selectionChanged}
      >
        <TableHeader>
          <Column className="sk:w-9">
            <span className="sk:sr-only">Selected</span>
          </Column>
          <Column isRowHeader>Service</Column>
          <Column className={cn('sk:w-24', NUMERIC)}>Price</Column>
          <Column className={cn('sk:w-16', NUMERIC)}>Days</Column>
          <Column className={cn('sk:w-28', NUMERIC)}>Delivery</Column>
        </TableHeader>
        <TableBody>
          {allowAutomatic ? (
            <Row id="any">
              <Cell>
                <RadioIndicator isSelected={selectedKey === 'any'} />
              </Cell>
              <Cell>
                <span className="sk:flex sk:flex-col">
                  <span className="sk:font-medium">Automatic</span>
                  <span className="sk:text-xs sk:text-fg-tertiary">
                    Our system picks the best available option at purchase
                  </span>
                </span>
              </Cell>
              <Cell />
              <Cell />
              <Cell />
            </Row>
          ) : null}
          {options.map(option => (
            <Row
              key={option.key}
              id={option.key}
              className={option.isDisabled ? 'sk:opacity-50' : ''}
            >
              <Cell>
                <RadioIndicator isSelected={selectedKey === option.key} />
              </Cell>
              <Cell>
                <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-2">
                  <img
                    src={option.icon}
                    alt={option.carrierName}
                    className="sk:size-4 sk:shrink-0"
                  />
                  {option.ownAccountLast4 ? (
                    <OwnCarrierAccountMark
                      carrierName={option.carrierName}
                      last4={option.ownAccountLast4}
                      workspaceName={orgName}
                      workspaceLogo={orgLogo}
                    />
                  ) : null}
                  <span className="sk:truncate">{option.serviceName}</span>
                  {option.isDisabled ? (
                    <Badge tone="neutral">No label</Badge>
                  ) : null}
                </span>
              </Cell>
              <Cell
                className={cn(
                  'sk:font-medium',
                  option.key === cheapestKey ? 'sk:text-success' : '',
                  NUMERIC,
                )}
              >
                {rateText(option.rate, currency)}
              </Cell>
              <Cell className={cn('sk:text-fg-secondary', NUMERIC)}>
                {option.days ? `${option.days}d` : '—'}
              </Cell>
              <Cell className={cn('sk:text-fg-secondary', NUMERIC)}>
                {option.delivery
                  ? DateUtility.formatDate(option.delivery, 'ddd, MMM D')
                  : '—'}
              </Cell>
            </Row>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
